// src/Components/Modules/CompanySetting/FestivalManagement.js
import React, { useState, useEffect, useContext } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import {
  Box, Typography, Paper, Grid, Avatar, Button, IconButton, Tooltip,
  Chip, Stack, Switch, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, InputAdornment, Alert, 
} from "@mui/material";
import {
  FaGift, FaCalendarAlt, FaPlus, FaEdit, FaTrash, FaEye, FaPercent,
  FaCheckCircle, FaTimesCircle, FaUsers, FaSave, FaClock, FaTag,
  FaMoneyBillWave, FaCalendar, FaStar, FaInfoCircle
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const ResponsiveTableWrapper = styled(Box)(({ theme }) => ({
  overflowX: "auto",
  borderRadius: theme.spacing(2),
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  "&::-webkit-scrollbar": {
    height: 8,
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: 10,
  },
  "&::-webkit-scrollbar-thumb": {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: 10,
    "&:hover": {
      background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
    },
  },
}));

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  fontWeight: 500,
  fontSize: "0.875rem",
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1, 1.5),
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
  },
}));

const ModernTableHead = styled(TableHead)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  "& .MuiTableCell-root": {
    color: "white",
    fontWeight: 700,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: theme.spacing(1.5, 2),
    [theme.breakpoints.down("md")]: {
      fontSize: "0.7rem",
      padding: theme.spacing(1, 1.5),
    },
  },
}));

const ModernTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": { backgroundColor: "rgba(102, 126, 234, 0.04)" },
  transition: "background-color 0.2s ease",
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: "white",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
}));

const employmentTypes = [
  { label: "Permanent", value: "permanent", color: "#2196f3" },
  { label: "Contractual", value: "contractual", color: "#ff9800" },
  { label: "Part-Time", value: "part_time", color: "#9c27b0" },
  { label: "Intern", value: "intern", color: "#4caf50" },
  { label: "Probation", value: "probation", color: "#f44336" },
  { label: "Temporary", value: "temporary", color: "#795548" },
  { label: "Freelance", value: "freelance", color: "#00bcd4" },
];

const FestivalManagement = () => {
  const { company } = useContext(MotherContext);
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFestival, setEditingFestival] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 960);
  const [formData, setFormData] = useState({
    festival_name: "",
    festival_date: "",
    festival_month: "",
    bonus_percentage: 100,
    min_service_months: 6,
    eligible_employment_types: [],
    is_active: true,
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 960);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchFestivals = async () => {
    setLoading(true);
    try {
      const res = await AxiosAuthInstance.get("company/festivals");
      setFestivals(res.data.festivals || []);
    } catch (error) {
      console.error("Failed to fetch festivals", error);
      Swal.fire({ icon: "error", title: "Error", text: "Failed to load festivals" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFestivals(); }, []);

  const handleOpenDialog = (festival = null) => {
    if (festival) {
      setEditingFestival(festival);
      setFormData({
        festival_name: festival.festival_name || "",
        festival_date: festival.festival_date || "",
        festival_month: festival.festival_month || "",
        bonus_percentage: festival.bonus_percentage ?? 100,
        min_service_months: festival.min_service_months ?? 6,
        eligible_employment_types: festival.eligible_employment_types || [],
        is_active: festival.is_active ?? true,
      });
    } else {
      setEditingFestival(null);
      setFormData({
        festival_name: "",
        festival_date: "",
        festival_month: "",
        bonus_percentage: 100,
        min_service_months: 6,
        eligible_employment_types: [],
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingFestival(null);
  };

  const handleSave = async () => {
    if (!formData.festival_name.trim()) {
      Swal.fire({ icon: "warning", title: "Missing Information", text: "Please enter festival name" });
      return;
    }
    if (formData.bonus_percentage <= 0) {
      Swal.fire({ icon: "warning", title: "Invalid Bonus", text: "Bonus percentage must be greater than 0" });
      return;
    }

    setLoading(true);
    try {
      const url = editingFestival
        ? `company/festivals/${editingFestival.id}`
        : "company/festivals";
      const method = editingFestival ? "put" : "post";

      const res = await AxiosAuthInstance[method](url, formData);

      if (res.data.status) {
        Swal.fire({
          icon: "success",
          title: editingFestival ? "Updated!" : "Added!",
          text: `Festival ${editingFestival ? "updated" : "added"} successfully`,
          timer: 2000,
          showConfirmButton: false,
        });
        fetchFestivals();
        handleCloseDialog();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Operation failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Festival?",
      text: "This action cannot be undone. Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await AxiosAuthInstance.delete(`company/festivals/${id}`);
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Festival deleted successfully",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchFestivals();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to delete festival" });
      }
    }
  };

  const toggleStatus = async (id) => {
    try {
      await AxiosAuthInstance.post(`company/festivals/${id}/toggle`);
      fetchFestivals();
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to update status" });
    }
  };

  const handleEligibleTypesChange = (type) => {
    setFormData(prev => ({
      ...prev,
      eligible_employment_types: prev.eligible_employment_types.includes(type)
        ? prev.eligible_employment_types.filter(t => t !== type)
        : [...prev.eligible_employment_types, type],
    }));
  };

  const getEmploymentTypeColor = (value) => {
    return employmentTypes.find(t => t.value === value)?.color || "#757575";
  };

  const getEmploymentTypeLabel = (value) => {
    return employmentTypes.find(t => t.value === value)?.label || value;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Box>
      <SectionCard>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar sx={{ bgcolor: "#fff3e0", color: "#ef6c00", mr: 2, width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 } }}>
              <FaGift size={isMobile ? 16 : 20} />
            </Avatar>
            <Box>
              <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600}>
                Festival Allowance Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                Manage festival bonuses for your employees
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<FaPlus />}
            onClick={() => handleOpenDialog()}
            size={isMobile ? "small" : "medium"}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              textTransform: "none",
              borderRadius: 2,
              px: { xs: 2, md: 3 },
              py: { xs: 0.8, md: 1.2 },
              fontWeight: 600,
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
            }}
          >
            Add New Festival
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <ResponsiveTableWrapper>
          <TableContainer>
            <Table size={isMobile ? "small" : "medium"}>
              <ModernTableHead>
                <TableRow>
                  <ModernTableCell>Festival</ModernTableCell>
                  <ModernTableCell>Date</ModernTableCell>
                  <ModernTableCell>Month</ModernTableCell>
                  <ModernTableCell>Bonus %</ModernTableCell>
                  <ModernTableCell>Min Service</ModernTableCell>
                  <ModernTableCell>Eligible Types</ModernTableCell>
                  <ModernTableCell align="center">Status</ModernTableCell>
                  <ModernTableCell align="center">Actions</ModernTableCell>
                </TableRow>
              </ModernTableHead>
              <TableBody>
                {festivals.length === 0 ? (
                  <TableRow>
                    <ModernTableCell colSpan={8} align="center" sx={{ py: { xs: 4, md: 8 } }}>
                      <Box sx={{ textAlign: "center" }}>
                        <FaGift style={{ fontSize: isMobile ? 40 : 56, color: "#bdbdbd", marginBottom: 16 }} />
                        <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary" gutterBottom>
                          No festivals configured
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.7rem", md: "0.875rem" } }}>
                          Click "Add New Festival" to get started
                        </Typography>
                      </Box>
                    </ModernTableCell>
                  </TableRow>
                ) : (
                  festivals.map((festival) => (
                    <ModernTableRow key={festival.id}>
                      <ModernTableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 1.5 } }}>
                          <Avatar sx={{ bgcolor: "#ffe0b2", width: { xs: 32, md: 40 }, height: { xs: 32, md: 40 } }}>
                            <FaGift style={{ color: "#f57c00", fontSize: isMobile ? 12 : 16 }} />
                          </Avatar>
                          <Typography fontWeight={600} sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                            {festival.festival_name}
                          </Typography>
                        </Box>
                      </ModernTableCell>
                      <ModernTableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <FaCalendarAlt style={{ color: "#667eea", fontSize: 12 }} />
                          <Typography variant="body2" sx={{ fontSize: { xs: "0.7rem", md: "0.875rem" } }}>
                            {formatDate(festival.festival_date)}
                          </Typography>
                        </Box>
                      </ModernTableCell>
                      <ModernTableCell>
                        <Typography variant="body2" sx={{ textTransform: "capitalize", fontSize: { xs: "0.7rem", md: "0.875rem" } }}>
                          {festival.festival_month || "N/A"}
                        </Typography>
                      </ModernTableCell>
                      <ModernTableCell>
                        <Chip
                          icon={<FaPercent style={{ fontSize: 12 }} />}
                          label={`${festival.bonus_percentage}%`}
                          size="small"
                          sx={{
                            bgcolor: "#e8f5e9",
                            color: "#2e7d32",
                            fontWeight: 600,
                            fontSize: { xs: "0.65rem", md: "0.875rem" },
                            height: { xs: 22, md: 28 },
                            "& .MuiChip-icon": { color: "#4caf50" },
                          }}
                        />
                      </ModernTableCell>
                      <ModernTableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <FaClock style={{ color: "#ff9800", fontSize: 12 }} />
                          <Typography variant="body2" sx={{ fontSize: { xs: "0.7rem", md: "0.875rem" } }}>
                            {festival.min_service_months} months
                          </Typography>
                        </Box>
                      </ModernTableCell>
                      <ModernTableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
                          {(festival.eligible_employment_types || []).length > 0 ? (
                            festival.eligible_employment_types.slice(0, isMobile ? 2 : 3).map((type) => (
                              <Chip
                                key={type}
                                label={getEmploymentTypeLabel(type)}
                                size="small"
                                sx={{
                                  bgcolor: `${getEmploymentTypeColor(type)}20`,
                                  color: getEmploymentTypeColor(type),
                                  fontSize: { xs: "0.6rem", md: "0.75rem" },
                                  fontWeight: 600,
                                  height: { xs: 20, md: 24 },
                                }}
                              />
                            ))
                          ) : (
                            <Chip
                              label="All Types"
                              size="small"
                              sx={{
                                bgcolor: "#e3f2fd",
                                color: "#1976d2",
                                fontSize: { xs: "0.6rem", md: "0.75rem" },
                                fontWeight: 600,
                                height: { xs: 20, md: 24 },
                              }}
                            />
                          )}
                          {(festival.eligible_employment_types || []).length > (isMobile ? 2 : 3) && (
                            <Chip
                              label={`+${festival.eligible_employment_types.length - (isMobile ? 2 : 3)}`}
                              size="small"
                              sx={{ 
                                bgcolor: "#f5f5f5", 
                                color: "#757575", 
                                fontSize: { xs: "0.6rem", md: "0.75rem" },
                                height: { xs: 20, md: 24 },
                              }}
                            />
                          )}
                        </Stack>
                      </ModernTableCell>
                      <ModernTableCell align="center">
                        <Switch
                          checked={festival.is_active}
                          onChange={() => toggleStatus(festival.id)}
                          color="success"
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            "& .MuiSwitch-thumb": {
                              background: festival.is_active
                                ? "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
                                : "#bdbdbd",
                            },
                          }}
                        />
                      </ModernTableCell>
                      <ModernTableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(festival)}
                              sx={{
                                color: "#667eea",
                                bgcolor: "rgba(102, 126, 234, 0.1)",
                                "&:hover": { bgcolor: "rgba(102, 126, 234, 0.2)" },
                                width: { xs: 28, md: 32 },
                                height: { xs: 28, md: 32 },
                              }}
                            >
                              <FaEdit style={{ fontSize: isMobile ? 10 : 12 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(festival.id)}
                              sx={{
                                color: "#f44336",
                                bgcolor: "rgba(244, 67, 54, 0.1)",
                                "&:hover": { bgcolor: "rgba(244, 67, 54, 0.2)" },
                                width: { xs: 28, md: 32 },
                                height: { xs: 28, md: 32 },
                              }}
                            >
                              <FaTrash style={{ fontSize: isMobile ? 10 : 12 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </ModernTableCell>
                    </ModernTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </ResponsiveTableWrapper>
      </SectionCard>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ 
          sx: { 
            borderRadius: { xs: 0, sm: 3 },
            m: { xs: 0, sm: 2 }
          } 
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            py: { xs: 2, md: 2.5 },
            px: { xs: 2, md: 3 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaGift />
            </Box>
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}>
              {editingFestival ? "Edit Festival" : "Add New Festival"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2.5}>
              {/* Basic Information Section */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 1.5, color: "#667eea" }}>
                  <FaTag style={{ marginRight: 6, verticalAlign: "middle" }} />
                  Basic Information
                </Typography>
                <Grid container spacing={{ xs: 2, md: 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Festival Name *"
                      value={formData.festival_name}
                      onChange={(e) => setFormData({ ...formData, festival_name: e.target.value })}
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaGift style={{ color: "#757575" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Festival Month"
                      value={formData.festival_month}
                      onChange={(e) => setFormData({ ...formData, festival_month: e.target.value })}
                      placeholder="e.g., Ramadan, December"
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaCalendar style={{ color: "#757575" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Festival Date"
                      type="date"
                      value={formData.festival_date}
                      onChange={(e) => setFormData({ ...formData, festival_date: e.target.value })}
                      size={isMobile ? "small" : "medium"}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaCalendarAlt style={{ color: "#757575" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Bonus Percentage *"
                      type="number"
                      value={formData.bonus_percentage}
                      onChange={(e) => setFormData({ ...formData, bonus_percentage: Number(e.target.value) })}
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaMoneyBillWave style={{ color: "#757575" }} />
                          </InputAdornment>
                        ),
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      inputProps={{ min: 0, max: 500 }}
                      helperText="100% = 1 month basic, 50% = half month"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Minimum Service (Months) *"
                      type="number"
                      value={formData.min_service_months}
                      onChange={(e) => setFormData({ ...formData, min_service_months: Number(e.target.value) })}
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaClock style={{ color: "#757575" }} />
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 0, max: 120 }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Eligibility Section */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 1.5, color: "#667eea" }}>
                  <FaUsers style={{ marginRight: 6, verticalAlign: "middle" }} />
                  Eligible Employment Types
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                  Select which employment types are eligible. Leave empty for all types.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: { xs: 0.5, md: 1 } }}>
                  {employmentTypes.map((type) => {
                    const isSelected = formData.eligible_employment_types.includes(type.value);
                    return (
                      <Chip
                        key={type.value}
                        label={type.label}
                        onClick={() => handleEligibleTypesChange(type.value)}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          cursor: "pointer",
                          borderRadius: 2,
                          px: 1,
                          fontWeight: 600,
                          fontSize: { xs: "0.7rem", md: "0.875rem" },
                          bgcolor: isSelected ? `${type.color}20` : "#f5f5f5",
                          color: isSelected ? type.color : "#757575",
                          border: `2px solid ${isSelected ? type.color : "transparent"}`,
                          "&:hover": {
                            bgcolor: isSelected ? `${type.color}30` : "#eeeeee",
                          },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>

              <Alert severity="info" icon={<FaInfoCircle />} sx={{ borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                  <strong>Note:</strong> Only employees who have completed the minimum service period will be eligible for this festival bonus.
                </Typography>
              </Alert>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 2.5 }, pt: 1, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1.2,
              borderRadius: 2,
              borderColor: "#757575",
              color: "#757575",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            variant="contained"
            size={isMobile ? "small" : "medium"}
            startIcon={loading ? null : <FaSave />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1.2,
              borderRadius: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
              },
            }}
          >
            {loading ? "Saving..." : editingFestival ? "Update Festival" : "Add Festival"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FestivalManagement;